import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { 
  GitCompare, 
  Crown, 
  Plus, 
  X, 
  Home, 
  DollarSign, 
  Ruler, 
  TrendingUp, 
  TrendingDown,
  Target,
  Zap,
  ArrowRight,
  RefreshCw,
  BarChart3,
  Layers,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  Play,
  Settings2
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, Cell } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Scenario Card Component
function ScenarioCard({ scenario, isSelected, onSelect, onRemove, showRemove }) {
  const roi = scenario.metrics?.roi || 0;
  const spread = scenario.metrics?.spread || 0;
  const risk = scenario.metrics?.riskScore || 50;
  
  return (
    <div 
      className={`relative p-4 border transition-all cursor-pointer ${
        isSelected 
          ? 'bg-[#D4AF37]/[0.08] border-[#D4AF37]/50' 
          : 'bg-[#0A0A0A]/80 backdrop-blur-sm/50 border-white/[0.08] hover:border-white/[0.15]'
      }`}
      onClick={onSelect}
    >
      {showRemove && (
        <button 
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute top-2 right-2 text-white/40 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>
      )}
      
      <div className="flex items-start gap-3">
        <Checkbox checked={isSelected} className="mt-1" />
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium text-sm truncate">{scenario.name}</h4>
          <p className="text-white/40 text-xs mt-0.5 truncate">{scenario.address}</p>
          
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="min-w-0">
              <p className="text-[9px] text-white/30 uppercase">Spread</p>
              <p className={`text-xs font-mono truncate ${spread > 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
                {formatCurrency(spread)}
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-[9px] text-white/30 uppercase">ROI</p>
              <p className={`text-xs font-mono truncate ${roi > 20 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : roi > 10 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-[#FF3B30]'}`}>
                {roi.toFixed(1)}%
              </p>
            </div>
            <div className="min-w-0">
              <p className="text-[9px] text-white/30 uppercase">Risk</p>
              <p className={`text-xs font-mono truncate ${risk < 40 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : risk < 60 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-[#FF3B30]'}`}>
                {risk}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simulation Parameters Panel
function SimulationParameters({ params, onChange }) {
  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm/50 border border-white/[0.08] p-4 space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Settings2 className="w-4 h-4 text-[#D4AF37]" />
        <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Simulation Variables</h3>
      </div>
      
      <div className="space-y-4">
        <div>
          <Label className="text-white/50 text-xs">ARV Adjustment</Label>
          <div className="flex items-center gap-3 mt-1">
            <Slider 
              value={[params.arvAdjustment]} 
              onValueChange={([v]) => onChange({ ...params, arvAdjustment: v })}
              min={-20}
              max={20}
              step={1}
              className="flex-1"
            />
            <span className={`text-sm font-mono w-12 text-right ${params.arvAdjustment >= 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
              {params.arvAdjustment > 0 ? '+' : ''}{params.arvAdjustment}%
            </span>
          </div>
        </div>
        
        <div>
          <Label className="text-white/50 text-xs">Rehab Cost Variance</Label>
          <div className="flex items-center gap-3 mt-1">
            <Slider 
              value={[params.rehabVariance]} 
              onValueChange={([v]) => onChange({ ...params, rehabVariance: v })}
              min={-30}
              max={50}
              step={5}
              className="flex-1"
            />
            <span className={`text-sm font-mono w-12 text-right ${params.rehabVariance <= 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'}`}>
              {params.rehabVariance > 0 ? '+' : ''}{params.rehabVariance}%
            </span>
          </div>
        </div>
        
        <div>
          <Label className="text-white/50 text-xs">Holding Period (months)</Label>
          <div className="flex items-center gap-3 mt-1">
            <Slider 
              value={[params.holdingMonths]} 
              onValueChange={([v]) => onChange({ ...params, holdingMonths: v })}
              min={3}
              max={18}
              step={1}
              className="flex-1"
            />
            <span className="text-sm font-mono w-12 text-right text-white/70">
              {params.holdingMonths}mo
            </span>
          </div>
        </div>
        
        <div>
          <Label className="text-white/50 text-xs">Interest Rate</Label>
          <div className="flex items-center gap-3 mt-1">
            <Slider 
              value={[params.interestRate]} 
              onValueChange={([v]) => onChange({ ...params, interestRate: v })}
              min={8}
              max={18}
              step={0.5}
              className="flex-1"
            />
            <span className="text-sm font-mono w-12 text-right text-white/70">
              {params.interestRate}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Comparison Metric Row
function MetricRow({ label, icon: Icon, values, format = 'currency', higherIsBetter = true }) {
  const getValueClass = (value, allValues) => {
    const nums = allValues.filter(v => v !== null && v !== undefined);
    if (nums.length < 2) return 'text-white';
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    if (value === max) return higherIsBetter ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]';
    if (value === min) return higherIsBetter ? 'text-[#FF3B30]' : 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]';
    return 'text-white';
  };
  
  const formatValue = (v) => {
    if (v === null || v === undefined) return '--';
    if (format === 'currency') return formatCurrency(v);
    if (format === 'percent') return `${v.toFixed(1)}%`;
    if (format === 'number') return v.toLocaleString();
    return v;
  };
  
  return (
    <tr className="hover:bg-white/[0.06]/30 border-b border-white/[0.06]">
      <td className="p-3 text-white/50 text-sm">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" />}
          {label}
        </div>
      </td>
      {values.map((val, idx) => (
        <td key={idx} className={`p-3 font-mono text-sm ${getValueClass(val, values)}`}>
          {formatValue(val)}
        </td>
      ))}
    </tr>
  );
}

// Winner Badge
function WinnerBadge({ scenario }) {
  if (!scenario.isWinner) return null;
  
  return (
    <div className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-[9px] font-bold px-2 py-0.5 uppercase">
      Best
    </div>
  );
}

export default function StrategySimulationPage({ user, subscription, onLogout }) {
  const [deals, setDeals] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [simulationResults, setSimulationResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [simulationParams, setSimulationParams] = useState({
    arvAdjustment: 0,
    rehabVariance: 0,
    holdingMonths: 6,
    interestRate: 12
  });

  const tier = subscription?.tier || "starter";

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true });
      // Transform deals into scenarios with calculated metrics
      const scenarios = response.data.map(deal => ({
        id: deal.deal_id,
        name: deal.property_address?.split(',')[0] || 'Property',
        address: deal.property_address,
        deal: deal,
        metrics: calculateMetrics(deal, simulationParams)
      }));
      setDeals(scenarios);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics for a deal with simulation parameters
  const calculateMetrics = (deal, params) => {
    const purchasePrice = deal.purchase_price || deal.asking_price || 0;
    const arv = (deal.arv || purchasePrice * 1.3) * (1 + params.arvAdjustment / 100);
    const rehabCost = (deal.rehab_estimate || purchasePrice * 0.15) * (1 + params.rehabVariance / 100);
    
    const totalInvestment = purchasePrice + rehabCost;
    const holdingCosts = totalInvestment * (params.interestRate / 100) * (params.holdingMonths / 12);
    const sellingCosts = arv * 0.08; // 8% selling costs
    
    const spread = arv - totalInvestment - holdingCosts - sellingCosts;
    const cashRequired = purchasePrice * 0.25 + rehabCost; // 25% down + full rehab
    const roi = cashRequired > 0 ? (spread / cashRequired) * 100 : 0;
    
    // Risk score calculation
    const arvRatio = purchasePrice / arv;
    const rehabRatio = rehabCost / purchasePrice;
    let riskScore = 50;
    if (arvRatio > 0.75) riskScore += 20;
    else if (arvRatio < 0.65) riskScore -= 15;
    if (rehabRatio > 0.30) riskScore += 15;
    if (params.holdingMonths > 9) riskScore += 10;
    riskScore = Math.max(10, Math.min(90, riskScore));
    
    return {
      purchasePrice,
      arv,
      rehabCost,
      totalInvestment,
      holdingCosts,
      sellingCosts,
      spread,
      cashRequired,
      roi,
      riskScore,
      annualizedRoi: roi * (12 / params.holdingMonths)
    };
  };

  // Recalculate metrics when params change
  const scenariosWithMetrics = useMemo(() => {
    return deals.map(scenario => ({
      ...scenario,
      metrics: calculateMetrics(scenario.deal, simulationParams)
    }));
  }, [deals, simulationParams]);

  // Toggle scenario selection
  const toggleScenario = (scenarioId) => {
    if (selectedScenarios.includes(scenarioId)) {
      setSelectedScenarios(selectedScenarios.filter(id => id !== scenarioId));
    } else if (selectedScenarios.length < 4) {
      setSelectedScenarios([...selectedScenarios, scenarioId]);
    } else {
      toast.error("Maximum 4 scenarios for comparison");
    }
  };

  // Run simulation
  const runSimulation = async () => {
    if (selectedScenarios.length < 2) {
      toast.error("Select at least 2 transactions to simulate");
      return;
    }

    setSimulating(true);
    
    // Simulate locally with current parameters
    const selectedDeals = scenariosWithMetrics.filter(s => selectedScenarios.includes(s.id));
    
    // Find winner based on ROI
    const maxRoi = Math.max(...selectedDeals.map(s => s.metrics.roi));
    
    const results = selectedDeals.map(s => ({
      ...s,
      isWinner: s.metrics.roi === maxRoi && s.metrics.roi > 0
    }));
    
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    setSimulationResults(results);
    setSimulating(false);
    toast.success("Simulation complete");
  };

  // Reset simulation
  const resetSimulation = () => {
    setSimulationResults(null);
    setSelectedScenarios([]);
    setSimulationParams({
      arvAdjustment: 0,
      rehabVariance: 0,
      holdingMonths: 6,
      interestRate: 12
    });
  };

  // Feature gate removed

  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className="ml-64 flex-1 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">Strategy Simulation</h1>
            <p className="text-white/40 mt-1 text-sm">Compare multiple deals and simulate capital allocation strategies.</p>
          </div>
          <div className="flex items-center gap-3">
            {simulationResults && (
              <Button 
                onClick={resetSimulation}
                variant="outline"
                className="border-white/[0.08] text-white/50 hover:text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />Reset
              </Button>
            )}
            <Button 
              onClick={runSimulation} 
              disabled={selectedScenarios.length < 2 || simulating}
              className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold"
            >
              {simulating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />Simulating...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />Run Simulation
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6">
          {/* Left: Transaction Selection */}
          <div className="col-span-1 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-semibold text-white/40 uppercase tracking-wider">Select Transactions</h2>
              <span className="text-[10px] text-white/30">{selectedScenarios.length}/4</span>
            </div>
            
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {loading ? (
                <div className="text-center py-8 text-white/40">Loading...</div>
              ) : scenariosWithMetrics.length === 0 ? (
                <div className="text-center py-8 text-white/40 text-sm">
                  No transactions available
                </div>
              ) : (
                scenariosWithMetrics.map(scenario => (
                  <ScenarioCard
                    key={scenario.id}
                    scenario={scenario}
                    isSelected={selectedScenarios.includes(scenario.id)}
                    onSelect={() => toggleScenario(scenario.id)}
                    onRemove={() => toggleScenario(scenario.id)}
                    showRemove={selectedScenarios.includes(scenario.id)}
                  />
                ))
              )}
            </div>

            {/* Simulation Parameters */}
            <SimulationParameters 
              params={simulationParams} 
              onChange={setSimulationParams} 
            />
          </div>

          {/* Right: Results */}
          <div className="col-span-3">
            {!simulationResults ? (
              <div className="h-full flex items-center justify-center bg-black/50 border border-white/[0.06] rounded-2xl">
                <div className="text-center py-16">
                  <Layers className="w-16 h-16 text-white/30 mx-auto mb-4" />
                  <h2 className="text-xl font-bold text-white/50 mb-2">Strategy Simulation Lab</h2>
                  <p className="text-white/40 text-sm max-w-md mx-auto mb-6">
                    Select 2-4 transactions from the left, adjust simulation variables, then run the simulation to compare outcomes.
                  </p>
                  {selectedScenarios.length > 0 && (
                    <p className="text-[#D4AF37] text-sm">
                      {selectedScenarios.length} transaction{selectedScenarios.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Winner Summary */}
                <div className="bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 p-4">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-[#D4AF37] font-semibold">
                        Optimal Strategy: {simulationResults.find(r => r.isWinner)?.name || 'N/A'}
                      </p>
                      <p className="text-white/40 text-sm">
                        Highest projected ROI at {simulationResults.find(r => r.isWinner)?.metrics.roi.toFixed(1)}% with current simulation parameters
                      </p>
                    </div>
                  </div>
                </div>

                {/* Comparison Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {simulationResults.map(result => (
                    <div 
                      key={result.id} 
                      className={`relative p-4 border ${
                        result.isWinner 
                          ? 'bg-[#D4AF37]/[0.08] border-[#D4AF37]/50' 
                          : 'bg-black border-white/[0.06]'
                      }`}
                    >
                      <WinnerBadge scenario={result} />
                      <h4 className="text-white font-medium text-sm truncate mb-3">{result.name}</h4>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-white/40 text-xs">Spread</span>
                          <span className={`font-mono text-sm ${result.metrics.spread > 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}`}>
                            {formatCurrency(result.metrics.spread)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/40 text-xs">ROI</span>
                          <span className={`font-mono text-sm ${result.metrics.roi > 20 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : result.metrics.roi > 10 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-[#FF3B30]'}`}>
                            {result.metrics.roi.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/40 text-xs">Annualized</span>
                          <span className="font-mono text-sm text-white/70">
                            {result.metrics.annualizedRoi.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/40 text-xs">Risk Score</span>
                          <span className={`font-mono text-sm ${result.metrics.riskScore < 40 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : result.metrics.riskScore < 60 ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-[#FF3B30]'}`}>
                            {result.metrics.riskScore}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Detailed Comparison Table */}
                <div className="bg-black border border-white/[0.06] overflow-hidden">
                  <div className="p-4 border-b border-white/[0.06] flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-[#D4AF37]" />
                    <h3 className="text-sm font-semibold text-white">Detailed Comparison</h3>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#0A0A0A]/80 backdrop-blur-sm/50">
                          <th className="text-left p-3 text-white/40 text-xs uppercase tracking-wider w-40">Metric</th>
                          {simulationResults.map(result => (
                            <th key={result.id} className="text-left p-3 text-white text-sm">
                              <div className="flex items-center gap-2">
                                {result.isWinner && <CheckCircle className="w-4 h-4 text-[#D4AF37]" />}
                                <span className="truncate max-w-32">{result.name}</span>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <MetricRow 
                          label="Purchase Price" 
                          icon={DollarSign}
                          values={simulationResults.map(r => r.metrics.purchasePrice)}
                          higherIsBetter={false}
                        />
                        <MetricRow 
                          label="ARV (Adjusted)" 
                          icon={TrendingUp}
                          values={simulationResults.map(r => r.metrics.arv)}
                          higherIsBetter={true}
                        />
                        <MetricRow 
                          label="Rehab Cost" 
                          icon={null}
                          values={simulationResults.map(r => r.metrics.rehabCost)}
                          higherIsBetter={false}
                        />
                        <MetricRow 
                          label="Total Investment" 
                          icon={null}
                          values={simulationResults.map(r => r.metrics.totalInvestment)}
                          higherIsBetter={false}
                        />
                        <MetricRow 
                          label="Holding Costs" 
                          icon={null}
                          values={simulationResults.map(r => r.metrics.holdingCosts)}
                          higherIsBetter={false}
                        />
                        <MetricRow 
                          label="Cash Required" 
                          icon={null}
                          values={simulationResults.map(r => r.metrics.cashRequired)}
                          higherIsBetter={false}
                        />
                        <MetricRow 
                          label="Projected Spread" 
                          icon={Target}
                          values={simulationResults.map(r => r.metrics.spread)}
                          higherIsBetter={true}
                        />
                        <MetricRow 
                          label="ROI" 
                          icon={TrendingUp}
                          values={simulationResults.map(r => r.metrics.roi)}
                          format="percent"
                          higherIsBetter={true}
                        />
                        <MetricRow 
                          label="Annualized ROI" 
                          icon={Zap}
                          values={simulationResults.map(r => r.metrics.annualizedRoi)}
                          format="percent"
                          higherIsBetter={true}
                        />
                        <MetricRow 
                          label="Risk Score" 
                          icon={AlertTriangle}
                          values={simulationResults.map(r => r.metrics.riskScore)}
                          format="number"
                          higherIsBetter={false}
                        />
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Simulation Parameters Used */}
                <div className="bg-black/50 border border-white/[0.06] p-4">

                </div>

                {/* Visual Charts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Capital Allocation Bar Chart */}
                  <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-5">
                    <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">Capital Allocation</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={simulationResults.map(r => ({
                        name: r.name.length > 12 ? r.name.substring(0, 12) + '...' : r.name,
                        investment: r.metrics.totalInvestment,
                        cash: r.metrics.cashRequired
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                        <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px' }}
                          formatter={v => [`$${v.toLocaleString()}`, '']}
                        />
                        <Bar dataKey="investment" fill="#D4AF37" name="Total Investment" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="cash" fill="#0A84FF" name="Cash Required" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Deal Ranking Bar Chart */}
                  <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-5">
                    <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">Deal Ranking by Spread</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={[...simulationResults].sort((a, b) => b.metrics.spread - a.metrics.spread).map(r => ({
                        name: r.name.length > 12 ? r.name.substring(0, 12) + '...' : r.name,
                        spread: r.metrics.spread,
                        isWinner: r.isWinner
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} />
                        <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px' }}
                          formatter={v => [`$${v.toLocaleString()}`, 'Spread']}
                        />
                        <Bar dataKey="spread" radius={[4, 4, 0, 0]}>
                          {[...simulationResults].sort((a, b) => b.metrics.spread - a.metrics.spread).map((r, i) => (
                            <Cell key={i} fill={r.isWinner ? '#00FF88' : '#D4AF37'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Risk vs Return Scatter Plot */}
                  <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-5 md:col-span-2">
                    <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">Risk vs Return</h3>
                    <ResponsiveContainer width="100%" height={220}>
                      <ScatterChart>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                        <XAxis 
                          type="number" 
                          dataKey="risk" 
                          name="Risk" 
                          tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                          label={{ value: 'Risk Score', position: 'bottom', fill: 'rgba(255,255,255,0.3)', fontSize: 10, offset: -5 }}
                        />
                        <YAxis 
                          type="number" 
                          dataKey="roi" 
                          name="ROI" 
                          tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                          label={{ value: 'ROI %', angle: -90, position: 'insideLeft', fill: 'rgba(255,255,255,0.3)', fontSize: 10 }}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px' }}
                          formatter={(v, name) => [name === 'risk' ? v.toFixed(0) : `${v.toFixed(1)}%`, name === 'risk' ? 'Risk Score' : 'ROI']}
                          labelFormatter={() => ''}
                        />
                        <Scatter data={simulationResults.map(r => ({
                          risk: r.metrics.riskScore,
                          roi: r.metrics.roi,
                          name: r.name
                        }))}>
                          {simulationResults.map((r, i) => (
                            <Cell key={i} fill={r.isWinner ? '#00FF88' : '#D4AF37'} />
                          ))}
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                    <div className="flex items-center gap-4 mt-2 justify-center">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 bg-[#00FF88] rounded-full" />
                        <span className="text-[10px] text-white/40">Best Deal</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 bg-[#D4AF37] rounded-full" />
                        <span className="text-[10px] text-white/40">Other Deals</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-black/50 border border-white/[0.06] p-4">
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-2">Simulation Parameters</p>
                  <div className="flex items-center gap-6 text-sm">
                    <span className="text-white/50">
                      ARV: <span className={simulationParams.arvAdjustment >= 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF3B30]'}>
                        {simulationParams.arvAdjustment > 0 ? '+' : ''}{simulationParams.arvAdjustment}%
                      </span>
                    </span>
                    <span className="text-white/50">
                      Rehab: <span className={simulationParams.rehabVariance <= 0 ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'}>
                        {simulationParams.rehabVariance > 0 ? '+' : ''}{simulationParams.rehabVariance}%
                      </span>
                    </span>
                    <span className="text-white/50">
                      Hold: <span className="text-white/70">{simulationParams.holdingMonths} months</span>
                    </span>
                    <span className="text-white/50">
                      Rate: <span className="text-white/70">{simulationParams.interestRate}%</span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
